import React, { useState, useRef, useEffect } from 'react';
import { X, MapPin, Plus } from 'lucide-react';
import useRecommendations from '../../api/recommendations';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import Loader from '../ui/loader';
import { useSyncedUser } from '../../context/SyncedUserContext';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import { Input } from '../ui/input';

const RECOMMENDATION_TYPES = {
  RESTAURANT: "Restaurant",
  ACTIVITY: "Activity",
  CAFE: "Cafe",
  CULTURE: "Culture",
  OTHER: "Other",
};

const AddRecommendationModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { syncedUserData } = useSyncedUser();
  const user = syncedUserData?.user;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autocomplete, setAutocomplete] = useState(null);
  const { showNotification } = useNotification();
  const addressInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check for mobile screen size on mount and when window resizes
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API,
    libraries: ['places']
  });

  const recommendationsApi = useRecommendations();
  
  const [formData, setFormData] = useState({
    user_id: user.id,
    title: '',
    type: RECOMMENDATION_TYPES.RESTAURANT,
    description: '',
    address: '',
    rating: 0,
    lat: 51.2289,
    lng: 2.9097,
  });

  // Reset form data when modal is opened
  useEffect(() => {
    if (isOpen) {
      setFormData({
        user_id: user.id,
        title: '',
        type: RECOMMENDATION_TYPES.RESTAURANT,
        description: '',
        address: '',
        rating: 0,
        lat: 51.2289,
        lng: 2.9097,
      });
      setError('');
      
      // Focus the title input when modal opens
      if (titleInputRef.current) {
        setTimeout(() => {
          titleInputRef.current.focus();
        }, 300);
      }
    }
  }, [isOpen, user.id]);

  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        }));
      }
      if(place.rating){
        setFormData(prev => ({
          ...prev,
          rating: place.rating
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await recommendationsApi.createRecommendation(formData);
      showNotification(t('addRecommendation.notifications.success'), 'success');
      onSuccess();
      onClose();
    } catch (error) {
      setError(t('addRecommendation.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) return null;
  
  // Form content shared between mobile and desktop views
  const formContent = (id) => (
    <form id={id} onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-500 p-3 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <div className="space-y-2">
        <label 
          htmlFor="title" 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t('addRecommendation.form.title')}
        </label>
        <Input
          id="title"
          ref={titleInputRef}
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder={t('addRecommendation.form.titlePlaceholder')}
          required
          className="w-full"
        />
      </div>

      <div className="space-y-2">
        <label 
          htmlFor="type" 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t('addRecommendation.form.type')}
        </label>
        <select
          id="type"
          value={formData.type}
          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
          className="w-full p-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:border-primary focus:ring-2 focus:ring-primary/20"
          required
        >
          {Object.entries(RECOMMENDATION_TYPES).map(([key, value]) => (
            <option key={value} value={value}>
              {t(`addRecommendation.types.${key.toLowerCase()}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label 
          htmlFor="description" 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t('addRecommendation.form.description')}
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t('addRecommendation.form.descriptionPlaceholder')}
          rows={3}
          required
          className="w-full p-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      <div className="space-y-2">
        <label 
          htmlFor="address" 
          className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          <MapPin className="h-3.5 w-3.5" />
          {t('addRecommendation.form.address')}
        </label>
        <div className="relative">
          <Autocomplete
            onLoad={setAutocomplete}
            onPlaceChanged={handlePlaceSelect}
          >
            <Input
              id="address"
              ref={addressInputRef}
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder={t('addRecommendation.form.addressPlaceholder')}
              required
              className="w-full"
            />
          </Autocomplete>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {t('addRecommendation.form.addressHelp')}
        </p>
      </div>
    </form>
  );

  // Mobile view with drawer
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="max-h-[85vh] sm:max-h-[90vh]">
          <div className="flex flex-col h-[85vh] sm:h-auto">
            {/* Header */}
            <DrawerHeader className="border-b border-neutral-200 dark:border-neutral-700 pb-3">
              <div className="flex items-center justify-between">
                <DrawerTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Plus className="h-5 w-5 text-primary" />
                  <span>{t('addRecommendation.title')}</span>
                </DrawerTitle>
                <DrawerClose className="rounded-full p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                  <X className="h-4 w-4" />
                </DrawerClose>
              </div>
              <DrawerDescription className="mt-1">
                {t('addRecommendation.description')}
              </DrawerDescription>
            </DrawerHeader>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {formContent("recommendation-form-mobile")}
            </div>

            {/* Footer with action buttons */}
            <DrawerFooter className="border-t border-neutral-200 dark:border-neutral-700 pt-3 pb-5 sm:pb-6">
              <Button
                type="submit"
                form="recommendation-form-mobile"
                disabled={loading}
                className="w-full"
              >
                {loading ? t('addRecommendation.buttons.creating') : t('addRecommendation.buttons.create')}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="w-full mt-2">
                  {t('common.cancel')}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop view with dialog
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            <span>{t('addRecommendation.title')}</span>
          </DialogTitle>
          <DialogDescription>
            {t('addRecommendation.description')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="pt-2">
          {formContent("recommendation-form-desktop")}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0 mt-6">
          <Button variant="outline" type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            form="recommendation-form-desktop" 
            disabled={loading}
          >
            {loading ? t('addRecommendation.buttons.creating') : t('addRecommendation.buttons.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRecommendationModal;